import { ArrowRight } from 'lucide-react';

import { ScrollReveal, SplitReveal } from '@/components/motion';
import { PDCA_CLOSING, PDCA_INTRO, PDCA_PHASES, QUALITY_STATEMENT } from '@/data/about';

/**
 * QualityAssurance (/about — section 6) — "A cycle of continuous improvement."
 *
 * Opens with the verbatim QUALITY_STATEMENT as the section lede, then frames
 * the Plan-Do-Check-Act cycle with the verbatim PDCA_INTRO. The four
 * PDCA_PHASES render as a connected four-step loop — plain hover-lift cards,
 * each numbered and carrying its phase Icon, title and verbatim body, joined
 * by directional arrows on desktop (Plan → Do → Check → Act) with a small
 * "cycle repeats" note standing in for the Act → Plan return. Closes with the
 * verbatim PDCA_CLOSING as an emphasised statement. Every institutional claim
 * comes from '@/data/about'; only connective microcopy (eyebrow, headline,
 * numbering, "the cycle repeats") is authored here.
 *
 * Motion: SplitReveal headline, ScrollReveal lede + child-stagger for the
 * phase cards and the closing statement. All primitives are reduced-motion
 * safe and never strand content hidden.
 */
export function QualityAssurance() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + verbatim statement ─────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Quality assurance
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            A cycle of continuous improvement.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              {QUALITY_STATEMENT}
            </p>
          </ScrollReveal>
        </header>

        {/* ── PDCA intro ────────────────────────────────────────────────── */}
        <ScrollReveal from="up" delay={0.12}>
          <p className="mt-10 max-w-3xl border-l-2 border-brand-gold/30 pl-6 text-base leading-relaxed text-muted-foreground md:text-lg">
            {PDCA_INTRO}
          </p>
        </ScrollReveal>

        {/* ── The PDCA loop — four connected steps ─────────────────────── */}
        <ScrollReveal
          stagger={0.1}
          className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4"
        >
          {PDCA_PHASES.map((phase, index) => (
            <div key={phase.phase} className="relative">
              <div className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0">
                <div className="flex items-center justify-between">
                  <div className="flex size-12 items-center justify-center rounded-xl bg-brand-navy/5 text-brand-navy">
                    <phase.Icon aria-hidden className="size-6" />
                  </div>
                  <span className="font-display text-sm font-semibold text-brand-gold tabular-nums">
                    {`0${index + 1}`}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl leading-tight font-semibold text-foreground">
                  {phase.phase}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{phase.body}</p>
              </div>

              {index < PDCA_PHASES.length - 1 ? (
                <div
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 -right-4 z-10 hidden -translate-y-1/2 lg:block"
                >
                  <ArrowRight className="size-6 text-brand-gold/50" />
                </div>
              ) : null}
            </div>
          ))}
        </ScrollReveal>

        <ScrollReveal from="up" delay={0.1}>
          <p className="mt-6 text-center font-label text-xs tracking-[0.22em] text-muted-foreground uppercase">
            Act feeds back into Plan — the cycle repeats.
          </p>
        </ScrollReveal>

        {/* ── Closing statement ────────────────────────────────────────── */}
        <ScrollReveal from="up" delay={0.15}>
          <p className="mt-14 border-t border-border pt-8 font-display text-xl leading-snug text-foreground md:text-2xl">
            {PDCA_CLOSING}
          </p>
        </ScrollReveal>
      </div>
    </section>
  );
}
